import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLElement} resetButton
 * @property {HTMLElement[]} [resultsItems]
 * @property {HTMLElement} [recentlyViewedWrapper]
 * @property {HTMLElement[]} [recentlyViewedTitle]
 * @property {HTMLElement[]} [recentlyViewedItems]
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  #activeFetch = null;
  #emptyStateLoaded = false;
  #defaultResultsMarkup = '';
  #closeResetTimer = null;
  #isKeyboardNavigation = false;

  /** @returns {DialogComponent | null} */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    // Save initial 4-product markup
    this.#defaultResultsMarkup = this.refs.predictiveSearchResults.innerHTML;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, {
        signal,
      });

      dialog.addEventListener(
        DialogCloseEvent.eventName,
        this.#handleDialogClose,
        {
          signal,
        }
      );

      dialog.addEventListener(
        DialogOpenEvent.eventName,
        this.#handleDialogOpen,
        {
          signal,
        }
      );

      this.addEventListener('click', this.#handleModalClick, {
        signal,
      });
    }

    if (
      RecentlyViewed.getProducts().length > 0 &&
      typeof requestIdleCallback === 'function'
    ) {
      requestIdleCallback(() => this.#loadEmptyState());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();

    if (this.#closeResetTimer !== null) {
      window.clearTimeout(this.#closeResetTimer);
      this.#closeResetTimer = null;
    }

    if (this.#activeFetch) {
      this.#activeFetch.abort();
      this.#activeFetch = null;
    }
  }

  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const interactive =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!interactive && this.refs.searchInput) {
      this.refs.searchInput.focus({
        preventScroll: true,
      });
    }
  };

  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * CLOSE BLINK FIX
   *
   * Modal close hote waqt DOM turant replace nahi hoga.
   * Pehle close animation finish hogi.
   */
  #handleDialogClose = () => {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
      this.#activeFetch = null;
    }

    if (this.#closeResetTimer !== null) {
      window.clearTimeout(this.#closeResetTimer);
    }

    this.#closeResetTimer = window.setTimeout(() => {
      const {
        predictiveSearchResults,
        searchInput,
      } = this.refs;

      this.#currentIndex = -1;
      this.#isKeyboardNavigation = false;

      searchInput.value = '';

      this.#hideResetButton();

      if (this.#defaultResultsMarkup) {
        predictiveSearchResults.innerHTML =
          this.#defaultResultsMarkup;

        this.#resetScrollPositions();
      }

      this.#closeResetTimer = null;
    }, 420);
  };

  /**
   * Every time modal opens
   */
  #handleDialogOpen = () => {
    if (this.#closeResetTimer !== null) {
      window.clearTimeout(this.#closeResetTimer);
      this.#closeResetTimer = null;
    }

    const {
      predictiveSearchResults,
      searchInput,
    } = this.refs;

    this.#currentIndex = -1;
    this.#isKeyboardNavigation = false;

    searchInput.value = '';

    this.#hideResetButton();

    if (this.#defaultResultsMarkup) {
      predictiveSearchResults.innerHTML =
        this.#defaultResultsMarkup;

      this.#resetScrollPositions();
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        searchInput.focus({
          preventScroll: true,
        });
      });
    });
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;

    this.#emptyStateLoaded = true;

    if (!this.#defaultResultsMarkup) {
      this.resetSearch(false);
    }
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    return /** @type {HTMLElement[]} */ (
      containers
        .flatMap((container) => {
          if (
            container.classList.contains(
              'predictive-search-results__wrapper-products'
            )
          ) {
            return Array.from(
              container.querySelectorAll(
                '.predictive-search-results__card'
              )
            );
          }

          return Array.from(
            container.querySelectorAll(
              '[ref="resultsItems[]"], .predictive-search-results__card'
            )
          );
        })
        .filter(
          (item) =>
            item instanceof HTMLElement
        )
    );
  }

  get #currentIndex() {
    return this.#allResultsItems.findIndex(
      (item) =>
        item.getAttribute(
          'aria-selected'
        ) === 'true'
    );
  }

  set #currentIndex(index) {
    const items =
      this.#allResultsItems;

    if (!items.length) return;

    let activeItem = null;

    items.forEach(
      (item, itemIndex) => {
        item.classList.remove(
          'keyboard-focus'
        );

        if (itemIndex === index) {
          item.setAttribute(
            'aria-selected',
            'true'
          );

          if (
            this.#isKeyboardNavigation
          ) {
            item.classList.add(
              'keyboard-focus'
            );
          }

          activeItem = item;
        } else {
          item.removeAttribute(
            'aria-selected'
          );
        }
      }
    );

    activeItem?.scrollIntoView({
      behavior: prefersReducedMotion()
        ? 'instant'
        : 'smooth',
      block: 'nearest',
    });

    this.refs.searchInput.focus({
      preventScroll: true,
    });
  }

  get #currentItem() {
    return this.#allResultsItems[
      this.#currentIndex
    ];
  }

  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    const items =
      this.#allResultsItems;

    if (
      !items.length ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight'
    ) {
      return;
    }

    const currentIndex =
      this.#currentIndex;

    const totalItems =
      items.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation =
          true;

        event.preventDefault();

        this.#currentIndex =
          currentIndex <
          totalItems - 1
            ? currentIndex + 1
            : 0;

        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation =
          true;

        event.preventDefault();

        this.#currentIndex =
          currentIndex > 0
            ? currentIndex - 1
            : totalItems - 1;

        break;

      case 'Tab':
        this.#isKeyboardNavigation =
          true;

        event.preventDefault();

        this.#currentIndex =
          event.shiftKey
            ? currentIndex > 0
              ? currentIndex - 1
              : totalItems - 1
            : currentIndex <
                totalItems - 1
              ? currentIndex + 1
              : 0;

        break;

      case 'Enter': {
        const singleResultContainer =
          this.refs.predictiveSearchResults.querySelector(
            '[data-single-result-url]'
          );

        if (
          singleResultContainer instanceof HTMLElement &&
          singleResultContainer.dataset.singleResultUrl
        ) {
          event.preventDefault();

          window.location.href =
            singleResultContainer.dataset.singleResultUrl;

          return;
        }

        if (
          this.#currentIndex >= 0
        ) {
          event.preventDefault();

          this.#currentItem
            ?.querySelector('a')
            ?.click();
        } else {
          const searchUrl =
            new URL(
              Theme.routes.search_url,
              location.origin
            );

          searchUrl.searchParams.set(
            'q',
            this.refs.searchInput.value
          );

          window.location.href =
            searchUrl.toString();
        }

        break;
      }
    }
  };

  clearRecentlyViewedProducts(
    event
  ) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const {
      recentlyViewedItems,
      recentlyViewedTitle,
      recentlyViewedWrapper,
    } = this.refs;

    const elements = [
      ...(recentlyViewedItems || []),
      ...(recentlyViewedTitle || []),
    ];

    if (!elements.length) return;

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add(
        'removing'
      );

      onAnimationEnd(
        recentlyViewedWrapper,
        () =>
          recentlyViewedWrapper.remove()
      );
    }
  }

  resetSearch = debounce(
    (keepFocus = true) => {
      if (keepFocus) {
        this.refs.searchInput.focus({
          preventScroll: true,
        });
      }

      this.#resetSearch();
    },
    100
  );

  /**
   * Search handler
   */
  search = debounce(
    (event) => {
      if (!event.inputType) return;

      const searchTerm =
        this.refs.searchInput.value.trim();

      this.#currentIndex = -1;

      if (!searchTerm.length) {
        this.#resetSearch();
        return;
      }

      this.#showResetButton();

      /**
       * First local search
       *
       * blo -> Blood Orange
       * rasp -> Raspberry
       * lem -> Lemonade
       * unf -> Unflavoured
       */
      if (
        this.#renderLocalProductMatches(
          searchTerm
        )
      ) {
        return;
      }

      /**
       * Shopify fallback
       */
      this.#getSearchResults(
        searchTerm
      );
    },
    120
  );

  /**
   * Local partial product search
   */
  #renderLocalProductMatches(
    searchTerm
  ) {
    if (
      !this.#defaultResultsMarkup
    ) {
      return false;
    }

    const parser =
      new DOMParser();

    const doc =
      parser.parseFromString(
        `
          <div id="local-search-root">
            ${this.#defaultResultsMarkup}
          </div>
        `,
        'text/html'
      );

    const root =
      doc.getElementById(
        'local-search-root'
      );

    if (!root) {
      return false;
    }

    const query =
      searchTerm
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();

    const cards =
      Array.from(
        root.querySelectorAll(
          '.search-mini-product'
        )
      );

    /**
     * If custom product cards aren't
     * found, use Shopify search.
     */
    if (!cards.length) {
      return false;
    }

    let matchCount = 0;

    cards.forEach((card) => {
      const title =
        card
          .querySelector(
            '.search-mini-product__title'
          )
          ?.textContent?.toLowerCase()
          .replace(/\s+/g, ' ')
          .trim() || '';

      if (
        title.includes(query)
      ) {
        matchCount += 1;
      } else {
        card.remove();
      }
    });

    const titleElement =
      root.querySelector(
        '.predictive-search-results__title'
      );

    if (matchCount > 0) {
      if (titleElement) {
        titleElement.textContent =
          matchCount === 1
            ? 'Matching Whey Protein'
            : 'Matching Whey Proteins';
      }

      this.refs.predictiveSearchResults.innerHTML =
        root.innerHTML;
    } else {
      const safeTerm =
        this.#escapeHtml(
          searchTerm
        );

      this.refs.predictiveSearchResults.innerHTML = `
        <div
          id="predictive-search-results"
          class="predictive-search-dropdown"
          role="listbox"
          aria-label="Search results"
          data-search-results
        >
          <div class="predictive-search-results__inner">

            <p class="predictive-search-results__no-results">
              No results found for "${safeTerm}". Try another search.
            </p>

          </div>
        </div>
      `;
    }

    this.#resetScrollPositions();

    return true;
  }

  #escapeHtml(value) {
    return value
      .replaceAll(
        '&',
        '&amp;'
      )
      .replaceAll(
        '<',
        '&lt;'
      )
      .replaceAll(
        '>',
        '&gt;'
      )
      .replaceAll(
        '"',
        '&quot;'
      )
      .replaceAll(
        "'",
        '&#039;'
      );
  }

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults
        .querySelector(
          '.predictive-search-results__inner'
        )
        ?.scrollTo(
          0,
          0
        );

      this.querySelector(
        '.predictive-search-form__content'
      )?.scrollTo(
        0,
        0
      );
    });
  }

  /**
   * Shopify predictive search
   */
  async #getSearchResults(
    searchTerm
  ) {
    if (
      !this.dataset.sectionId
    ) {
      return;
    }

    const url =
      new URL(
        Theme.routes.predictive_search_url,
        location.origin
      );

    url.searchParams.set(
      'q',
      searchTerm
    );

    url.searchParams.set(
      'resources[type]',
      'product'
    );

    url.searchParams.set(
      'resources[limit]',
      '4'
    );

    url.searchParams.set(
      'resources[limit_scope]',
      'each'
    );

    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,tag,variants.title,vendor'
    );

    url.searchParams.set(
      'resources[options][unavailable_products]',
      'show'
    );

    const {
      predictiveSearchResults,
    } = this.refs;

    const abortController =
      this.#createAbortController();

    sectionRenderer
      .getSectionHTML(
        this.dataset.sectionId,
        false,
        url
      )
      .then(
        (resultsMarkup) => {
          if (
            !resultsMarkup ||
            abortController.signal.aborted
          ) {
            return;
          }

          morph(
            predictiveSearchResults,
            resultsMarkup
          );

          this.#resetScrollPositions();
        }
      )
      .catch((error) => {
        if (
          abortController.signal.aborted
        ) {
          return;
        }

        throw error;
      });
  }

  async #getRecentlyViewedProductsMarkup() {
    if (
      !this.dataset.sectionId
    ) {
      return null;
    }

    const viewedProducts =
      RecentlyViewed.getProducts();

    if (
      !viewedProducts.length
    ) {
      return null;
    }

    const url =
      new URL(
        Theme.routes.search_url,
        location.origin
      );

    url.searchParams.set(
      'q',
      viewedProducts
        .map(
          (id) =>
            `id:${id}`
        )
        .join(' OR ')
    );

    url.searchParams.set(
      'resources[type]',
      'product'
    );

    return sectionRenderer.getSectionHTML(
      this.dataset.sectionId,
      false,
      url
    );
  }

  #hideResetButton() {
    this.refs.resetButton.hidden =
      true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden =
      false;
  }

  #createAbortController() {
    const abortController =
      new AbortController();

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    this.#activeFetch =
      abortController;

    return abortController;
  }

  /**
   * RESET SEARCH
   */
  #resetSearch = async () => {
    const {
      predictiveSearchResults,
      searchInput,
    } = this.refs;

    const emptySectionId =
      'predictive-search-empty';

    this.#currentIndex = -1;
    this.#isKeyboardNavigation = false;

    searchInput.value = '';

    this.#hideResetButton();

    const abortController =
      this.#createAbortController();

    /**
     * Restore original four products
     */
    if (
      this.#defaultResultsMarkup
    ) {
      predictiveSearchResults.innerHTML =
        this.#defaultResultsMarkup;

      this.#resetScrollPositions();

      return;
    }

    /**
     * Theme fallback
     */
    const url =
      new URL(
        window.location.href
      );

    url.searchParams.delete(
      'page'
    );

    const emptySectionMarkup =
      await sectionRenderer.getSectionHTML(
        emptySectionId,
        false,
        url
      );

    const parsedEmptySectionMarkup =
      new DOMParser()
        .parseFromString(
          emptySectionMarkup,
          'text/html'
        )
        .querySelector(
          '.predictive-search-empty-section'
        );

    if (
      !parsedEmptySectionMarkup
    ) {
      throw new Error(
        'No empty section markup found'
      );
    }

    const viewedProducts =
      RecentlyViewed.getProducts();

    if (
      viewedProducts.length > 0
    ) {
      const recentlyViewedMarkup =
        await this.#getRecentlyViewedProductsMarkup();

      if (
        !recentlyViewedMarkup
      ) {
        return;
      }

      const parsedRecentlyViewedMarkup =
        new DOMParser()
          .parseFromString(
            recentlyViewedMarkup,
            'text/html'
          );

      const recentlyViewedProductsHtml =
        parsedRecentlyViewedMarkup.getElementById(
          'predictive-search-products'
        );

      if (
        !recentlyViewedProductsHtml
      ) {
        return;
      }

      for (
        const child of
        recentlyViewedProductsHtml.children
      ) {
        if (
          child instanceof HTMLElement
        ) {
          child.setAttribute(
            'ref',
            'recentlyViewedWrapper'
          );
        }
      }

      const collectionElement =
        parsedEmptySectionMarkup.querySelector(
          '#predictive-search-products'
        );

      if (
        !collectionElement
      ) {
        return;
      }

      collectionElement.prepend(
        ...recentlyViewedProductsHtml.children
      );
    }

    if (
      abortController.signal.aborted
    ) {
      return;
    }

    morph(
      predictiveSearchResults,
      parsedEmptySectionMarkup
    );

    this.#resetScrollPositions();
  };
}

if (
  !customElements.get(
    'predictive-search-component'
  )
) {
  customElements.define(
    'predictive-search-component',
    PredictiveSearchComponent
  );
}